import { createContext, useContext } from 'react';
import type { NavTone } from '@/components/layout/shell/types';

/*
  Типы и контекст «Наблюдателя» (docs/observer-design.md, DESIGN.md R36).
  Вынесены из провайдера, чтобы файл провайдера экспортировал только компонент:
  иначе ломается горячая перезагрузка.

  Два дома (проп провайдера home):
  - orbit — площадка /design/observer: своё тело на орбите у края экрана, садится на углы блоков;
  - core — оболочка (spec-3 «Ядро-вестник»): своего тела нет, тело — ядро знака. Дом ядра — сфера в знаке
    на главной и коралловое выделение первого уровня в разделе (R41); событие — отклик дома, а с курьером
    (этап D2) — капля к месту события и обратно.
*/

/** Чем занята система: держится, пока состояние актуально. */
export type ObserverState =
  'idle' | 'thinking' | 'writing' | 'download' | 'upload' | 'update' | 'asking' | 'success' | 'error';

/** Долгое состояние в стопке дома: держится под ключом, пока его не снимут (resolve). */
export type HeldState = Exclude<ObserverState, 'idle' | 'success'>;

/** Внешняя форма. */
export type ObserverShape = 'dock' | 'dot' | 'planet';

/**
 * Как он относится к элементу:
 * cover — подсвечивает элемент целиком (оставлено для особых случаев),
 * edge — садится у края элемента,
 * corner — сидит на верхнем правом углу блока, наполовину снаружи: так он «на кромке»,
 * free — парит у края экрана.
 */
export type ObserverPlacement = 'cover' | 'edge' | 'corner' | 'free';

/** Насколько громко он присутствует. */
export type ObserverPresence = 'quiet' | 'companion';

/** Сила деформаций при движении. */
export type ObserverMotion = 'restrained' | 'pronounced' | 'cartoon';

/** Короткая реакция поверх состояния. */
export type ObserverGesture = 'bounce' | 'shake' | 'shiver' | 'flash' | 'nod' | 'pulse';

/** Типы событий, на которые он умеет отзываться. ask — нужен ответ человека (держится до resolve). */
export type ObserverEventKind = 'notification' | 'message' | 'error' | 'done' | 'update' | 'ask';

/** Роль элемента в мире Наблюдателя. */
export type AnchorRole = 'home' | 'block' | 'source' | 'action' | 'item' | 'field';

export interface AnchorMeta {
  role: AnchorRole;
  label: string;
  priority?: number;
  /** Уровень в цепочке навигации: 0 — раздел, 1 — подраздел, 2 — вкладка объекта, 3 — вкладка страницы. */
  level?: number;
}

/** Посадка тела на блок (spec-3-motion §2): левое плечо или правый угол — до ответа Максима переключателем. */
export type ObserverSeat = 'shoulder' | 'corner';

export interface ObserverSettings {
  enabled: boolean;
  presence: ObserverPresence;
  motion: ObserverMotion;
  followCursor: boolean;
  /** Подлетать к источнику события и показывать, где появилось. */
  guidance: boolean;
  /** Не беспокоить: цвет меняется, движения нет. */
  calm: boolean;
  /** Отвечать на нажатие (задел на помощника). */
  interactive: boolean;
  reactions: Record<ObserverEventKind, boolean>;
  /** Дом core: посадка тела на блок — левое плечо (по умолчанию) или правый угол. */
  seat: ObserverSeat;
  /**
   * Дом core: обводить цель кольцом — подсветка чужого элемента. Открытый вопрос Максиму (spec-3 §12),
   * поэтому по умолчанию выключено.
   */
  targetRing: boolean;
  /** Стенд: улицы дома пунктиром и выбранный маршрут (этап D2). */
  showRoute: boolean;
  /** Стенд: курьер, ядро и выделение вчетверо медленнее — проверить швы передачи (этап D2). */
  slowMo: boolean;
}

/**
 * Режим «Помощника» (spec-3 §9) — одна ручка поверх настроек: «Выкл» (enabled=false), «На месте»
 * (guidance=false: цвет, ритм, жесты дома), «Показывает, где» (guidance=true: ещё и путь к событию).
 */
export type AssistantMode = 'off' | 'home' | 'guide';

export function assistantMode(s: Pick<ObserverSettings, 'enabled' | 'guidance'>): AssistantMode {
  if (!s.enabled) return 'off';
  return s.guidance ? 'guide' : 'home';
}

export function assistantPatch(mode: AssistantMode): Partial<ObserverSettings> {
  return mode === 'off' ? { enabled: false } : { enabled: true, guidance: mode === 'guide' };
}

/** Событие системы. Признаки события (счётчик, точка, тост) ставит тот, кто владеет данными, — сразу. */
export interface ObserverEvent {
  kind: ObserverEventKind;
  /** Адрес источника в реестре якорей или цепочка адресов: берётся первый видимый. */
  anchorId?: string | string[];
  /**
   * Ключ события: тот же ключ за 1,5 с — один показ с числом; под ним держится «ждёт ответа» (resolve(key))
   * или операция, которую событие «готово» завершает.
   */
  key?: string;
  /** Строка для читалки и будущей подсказки. */
  label?: string;
  /** false — о событии уже говорит тост: строка читалки молчит, чтобы не было двойного объявления. */
  announce?: boolean;
}

/** Где ядро в покое, в экранных координатах: сфера в знаке или выделение пункта. */
export interface CoreOrigin {
  x: number;
  y: number;
  /** Радиус ядра (капля в пути — того же размера). */
  r: number;
  kind: 'mark' | 'pill';
  /** Метка data-core-home стоит: нет перелёта ядра, знакомства и загрузки — можно уходить. */
  rest: boolean;
  /** Дом виден: меню не спрятано («Фокус») и не закрыто слоем поверх. */
  shown: boolean;
  /** Тон дома (окраска по состоянию): капля уходит и возвращается в нём — шов передачи без смены цвета. */
  tone: NavTone;
}

/** Свой дом ядра в меню (spec-3-motion §1): прямоугольники в экранных координатах. */
export interface MenuLanes {
  menu: DOMRect;
  /** Видимая часть прокрутки пунктов. */
  scroll: DOMRect;
  /** Ось коридора — левое поле меню, x на экране. */
  hallX: number;
  /** Ось порога — полоса между строкой знака и первым пунктом, y на экране. */
  sillY: number;
  /** Низ коридора: над строкой «я» (её верх − 8), y на экране. */
  hallBottom: number;
  /** Пункты: адрес якоря (nav.<id>) и прямоугольник. */
  items: { id: string; rect: DOMRect }[];
  /** Ворота «порог»: справа от меню сразу окно (главная). */
  sillGate: boolean;
}

/** Жест дома: из языка Наблюдателя (motion.css, [data-gesture]) или кольцо вокруг ядра. */
export type HomeGesture = ObserverGesture | 'burst';

/** Разовый отклик дома: играет при смене at. */
export interface HomeCue {
  gesture: HomeGesture;
  at: number;
}

/**
 * Дом ядра — его отдаёт главное меню (MainMenu). Провайдер спрашивает, где ядро, просит отклик дома,
 * а курьер (этап D2) забирает ядро каплей и возвращает.
 */
export interface CorePort {
  /** Где ядро сейчас; null — меню ещё не на месте. */
  origin(): CoreOrigin | null;
  lanes(): MenuLanes;
  /** Отдать ядро: знак набухает, выделение стягивается в каплю; кончается кадром передачи. */
  release(dir: 'up' | 'down' | 'left'): Promise<void>;
  /** Принять каплю: ядро садится в знак или растекается в выделение. */
  receive(): Promise<void>;
  /** Отклик дома: на ядре в знаке или на выделении. */
  cue(c: HomeCue): void;
  /** Дом сменился (переход по меню, раскрытие, свёртывание); вернуть — отписка. */
  onChange(cb: () => void): () => void;
}

/** Реестр мира: элементы заявляют о себе сами. Стабилен — подписка на него не перерисовывает якоря. */
export interface ObserverRegistry {
  registerAnchor: (id: string, el: HTMLElement, meta: AnchorMeta) => void;
  /** Снять элемент с адреса; без el — все элементы адреса. */
  unregisterAnchor: (id: string, el?: HTMLElement) => void;
}

export interface ObserverApi extends ObserverRegistry {
  settings: ObserverSettings;
  /** Что показывает дом: верхнее состояние стопки или вспышка «готово» поверх неё. */
  state: ObserverState;
  shape: ObserverShape;
  /** Текущая цель по адресу; null — свободный полёт. */
  anchorId: string | null;
  placement: ObserverPlacement;
  onActivate: (() => void) | null;
  /** Какой дом: орбита площадки или ядро знака в оболочке. */
  home: 'orbit' | 'core';
  /** Ядро ушло к событию (меняется дважды за визит). */
  away: boolean;
  /** Долгие состояния под ключами: операция идёт, ждёт ответа, сбой. */
  holds: Readonly<Record<string, HeldState>>;
  /** Ритм ожидания дома: верхнее состояние «ждёт ответа», помощник включён, ядро дома. */
  awaiting: boolean;

  setSettings: (patch: Partial<ObserverSettings>) => void;
  setState: (s: ObserverState) => void;
  setShape: (s: ObserverShape) => void;
  setOnActivate: (fn: (() => void) | null) => void;

  /** Подержать долгое состояние под ключом (без отклика): операция идёт, вернулся вопрос. */
  hold: (key: string, state: HeldState) => void;
  /** Снять долгое состояние под ключом: ответ дан, операция остановлена. */
  resolve: (key: string) => void;
  /** Главное меню отдаёт дом ядра (или снимает, null). */
  setCorePort: (port: CorePort | null) => void;
  /** Элемент якоря по адресу, если он зарегистрирован. */
  anchor: (id: string) => HTMLElement | null;

  /** Сесть домой: самый глубокий активный уровень навигации. */
  goHome: () => void;
  /** Назначить дом (обычно активный пункт навигации). */
  setHome: (id: string | null) => void;
  /** Подлететь к элементу и показать его. */
  pointAt: (id: string, gesture?: ObserverGesture) => void;
  /** Жест на месте. */
  react: (gesture: ObserverGesture) => void;
  /** Событие системы: сам решит, лететь или отозваться на месте. */
  notify: (event: ObserverEvent) => void;

  rootRef: { current: HTMLDivElement | null };
  tiltRef: { current: HTMLDivElement | null };
  skinRef: { current: HTMLDivElement | null };
}

export const DEFAULT_SETTINGS: ObserverSettings = {
  enabled: true,
  presence: 'companion',
  motion: 'pronounced',
  followCursor: true,
  guidance: true,
  calm: false,
  interactive: false,
  reactions: { notification: true, message: true, error: true, done: true, update: true, ask: true },
  seat: 'shoulder',
  // Решение Максима 23.10: без кольца на карточке из нескольких строк не понять, какую имеет в виду помощник.
  targetRing: true,
  showRoute: false,
  slowMo: false,
};

export const ObserverCtx = createContext<ObserverApi | null>(null);
export const ObserverRegistryCtx = createContext<ObserverRegistry | null>(null);

export function useObserver() {
  const ctx = useContext(ObserverCtx);
  if (!ctx) throw new Error('useObserver вне ObserverProvider');
  return ctx;
}

/** Без провайдера — null: меню и панели живут и в галерее, и на основном сайте, где помощника нет. */
export function useObserverOptional(): ObserverApi | null {
  return useContext(ObserverCtx);
}

export function useObserverRegistry(): ObserverRegistry | null {
  return useContext(ObserverRegistryCtx);
}
